import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FilterListIcon from "@mui/icons-material/FilterList";
import VisibilityIcon from "@mui/icons-material/Visibility";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import { IOrchestrationInstanceSummary } from "../declarations/IOrchestrationInstance";

interface IProcessInstanceListProps {
    continuationToken: string | null;
    instances: IOrchestrationInstanceSummary[];
    isLoading: boolean;
    nameFilter: string;
    onLoadMore: () => void;
    onNameFilterChange: (value: string) => void;
    onStatusFilterChange: (statuses: string[]) => void;
    statusFilter: string[];
}

const STATUS_OPTIONS = ["Running", "Completed", "Failed", "Terminated", "Pending"];

type StatusColor = "primary" | "success" | "error" | "warning" | "default";

function getStatusColor(status: string): StatusColor {
    switch (status) {
        case "Running":
            return "primary";
        case "Completed":
            return "success";
        case "Failed":
            return "error";
        case "Terminated":
            return "warning";
        case "Pending":
        default:
            return "default";
    }
}

function timeAgo(dateString: string): string {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
}

function formatAbsoluteTime(dateString: string): string {
    return new Date(dateString).toLocaleString();
}

function getHumanReadableName(name: string): string {
    // Convert PascalCase/camelCase to spaced words
    return name.replace(/([A-Z])/g, " $1").trim();
}

export default function ProcessInstanceList({
    continuationToken,
    instances,
    isLoading,
    nameFilter,
    onLoadMore,
    onNameFilterChange,
    onStatusFilterChange,
    statusFilter,
}: IProcessInstanceListProps) {
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(false);

    const handleStatusToggle = (status: string) => {
        if (statusFilter.includes(status)) {
            onStatusFilterChange(statusFilter.filter((s) => s !== status));
        } else {
            onStatusFilterChange([...statusFilter, status]);
        }
    };

    const handleRowClick = (instanceId: string) => {
        navigate(`/monitor/${instanceId}`);
    };

    return (
        <Box>
            <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 2 }}
            >
                <Typography variant="h6">Orchestration Instances</Typography>
                <IconButton onClick={() => setShowFilters(!showFilters)}>
                    <FilterListIcon />
                </IconButton>
            </Stack>

            {showFilters && (
                <Stack spacing={2} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {STATUS_OPTIONS.map((status) => (
                            <Chip
                                color={
                                    statusFilter.includes(status)
                                        ? getStatusColor(status)
                                        : "default"
                                }
                                key={status}
                                label={status}
                                onClick={() => handleStatusToggle(status)}
                                variant={
                                    statusFilter.includes(status)
                                        ? "filled"
                                        : "outlined"
                                }
                            />
                        ))}
                    </Stack>
                    <TextField
                        label="Filter by orchestration name"
                        onChange={(e) => onNameFilterChange(e.target.value)}
                        size="small"
                        value={nameFilter}
                    />
                </Stack>
            )}

            {isLoading && <LinearProgress sx={{ mb: 2 }} />}

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Orchestration Name</TableCell>
                            <TableCell>Entity ID</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Started By</TableCell>
                            <TableCell>Started</TableCell>
                            <TableCell>Last Updated</TableCell>
                            <TableCell>Progress</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {instances.length === 0 && !isLoading && (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <Typography
                                        color="text.secondary"
                                        variant="body2"
                                    >
                                        No orchestration instances found.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {instances.map((instance) => (
                            <TableRow
                                hover
                                key={instance.instanceId}
                                onClick={() =>
                                    handleRowClick(instance.instanceId)
                                }
                                sx={{ cursor: "pointer" }}
                            >
                                <TableCell>
                                    {getHumanReadableName(instance.name)}
                                </TableCell>
                                <TableCell>
                                    {instance.customStatus?.csEntityID ?? "-"}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        color={getStatusColor(
                                            instance.runtimeStatus,
                                        )}
                                        label={instance.runtimeStatus}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    {instance.customStatus?.startedByUser ??
                                        "-"}
                                </TableCell>
                                <TableCell>
                                    <Tooltip
                                        title={formatAbsoluteTime(
                                            instance.createdAt,
                                        )}
                                    >
                                        <span>
                                            {timeAgo(instance.createdAt)}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    <Tooltip
                                        title={formatAbsoluteTime(
                                            instance.lastUpdatedAt,
                                        )}
                                    >
                                        <span>
                                            {timeAgo(instance.lastUpdatedAt)}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>
                                    {instance.runtimeStatus === "Running" && (
                                        <LinearProgress
                                            sx={{
                                                height: 6,
                                                borderRadius: 1,
                                                minWidth: 80,
                                            }}
                                            value={
                                                instance.customStatus
                                                    ?.percentComplete ?? 0
                                            }
                                            variant={
                                                instance.customStatus
                                                    ?.percentComplete
                                                    ? "determinate"
                                                    : "indeterminate"
                                            }
                                        />
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRowClick(
                                                instance.instanceId,
                                            );
                                        }}
                                        size="small"
                                    >
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {continuationToken && (
                <Box sx={{ mt: 2, textAlign: "center" }}>
                    <Button onClick={onLoadMore} variant="outlined">
                        Load More
                    </Button>
                </Box>
            )}
        </Box>
    );
}
